import type { SavedRouteEntry } from '../types';

const STORAGE_KEY = 'ontheway_saved_routes_v1';
const BUILTIN_SEED_FLAG_KEY = 'ontheway_saved_routes_builtin_seeded_v1';
const MAX_ROUTES = 25;
const DEDUPE_EPS = 0.001;

export const BUILTIN_EXAMPLE_ROUTE_ID = 'builtin-example-nj-oh-v1';

export type SaveRouteResult =
  | { ok: true; entry: SavedRouteEntry; duplicate: boolean }
  | { ok: false; reason: 'storage' | 'invalid' };

export const BUILTIN_EXAMPLE: Omit<SavedRouteEntry, 'id' | 'createdAt'> = {
  start: {
    displayName: 'Jersey City, NJ',
    latitude: 40.7282,
    longitude: -74.0776,
  },
  end: {
    displayName: 'Cleveland, OH',
    latitude: 41.4993,
    longitude: -81.6944,
  },
};

function withinDeg(a: number, b: number): boolean {
  return Math.abs(a - b) <= DEDUPE_EPS;
}

function samePoint(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): boolean {
  return withinDeg(a.latitude, b.latitude) && withinDeg(a.longitude, b.longitude);
}

export function sameOriginDestination(
  a: Pick<SavedRouteEntry, 'start' | 'end'>,
  b: Pick<SavedRouteEntry, 'start' | 'end'>,
): boolean {
  return samePoint(a.start, b.start) && samePoint(a.end, b.end);
}

function originDestOnly(entry: Pick<SavedRouteEntry, 'start' | 'end'>): Pick<SavedRouteEntry, 'start' | 'end'> {
  return {
    start: {
      displayName: entry.start.displayName,
      latitude: entry.start.latitude,
      longitude: entry.start.longitude,
    },
    end: {
      displayName: entry.end.displayName,
      latitude: entry.end.latitude,
      longitude: entry.end.longitude,
    },
  };
}

function readList(): SavedRouteEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((r) => r && r.id && r.start && r.end)
      .map((r) => ({
        id: r.id,
        createdAt: r.createdAt,
        ...originDestOnly(r),
      }));
  } catch {
    return [];
  }
}

function writeList(routes: SavedRouteEntry[]): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(routes));
    return true;
  } catch {
    return false;
  }
}

export function saveRoute(entry: Pick<SavedRouteEntry, 'start' | 'end'>): SaveRouteResult {
  const pair = originDestOnly(entry);
  if (!Number.isFinite(pair.start.latitude) || !Number.isFinite(pair.end.latitude)) {
    return { ok: false, reason: 'invalid' };
  }
  const existing = readList();
  const duplicate = existing.find((r) => sameOriginDestination(r, pair));
  const withoutDupes = existing.filter((r) => !sameOriginDestination(r, pair));
  const saved: SavedRouteEntry = {
    ...pair,
    id: duplicate?.id || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    createdAt: Date.now(),
  };
  const next = [saved, ...withoutDupes].slice(0, MAX_ROUTES);
  if (!writeList(next)) return { ok: false, reason: 'storage' };
  return { ok: true, entry: saved, duplicate: Boolean(duplicate) };
}

export function deleteSavedRoute(id: string): void {
  const existing = readList();
  writeList(existing.filter((r) => r.id !== id));
}

/** iOS 237286e dropped the JC→Cleveland seed. Remove it if it is still stored. */
export function purgeRemovedBuiltinExampleRoute(): void {
  try {
    const filtered = readList().filter((r) => r.id !== BUILTIN_EXAMPLE_ROUTE_ID);
    writeList(filtered);
    localStorage.removeItem(BUILTIN_SEED_FLAG_KEY);
  } catch {
    // ignore
  }
}

export function listSavedRoutes(): SavedRouteEntry[] {
  return readList().filter((r) => r.id !== BUILTIN_EXAMPLE_ROUTE_ID);
}

export function savedRouteLabel(entry: Pick<SavedRouteEntry, 'start' | 'end'>): string {
  const start = entry.start.displayName.split(',')[0] || entry.start.displayName;
  const end = entry.end.displayName.split(',')[0] || entry.end.displayName;
  return `${start} → ${end}`;
}
